import React, { useContext, useEffect, useRef, useState } from 'react';
import { useMountedState } from 'react-use';

import { Loader, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { getPhraseCommonness } from 'client/api';
import { getCommonnessError } from 'client/blocks/Form/TextInputWithCommonness/TextInputWithCommonness';
import { FormFieldContext } from 'client/context/formFieldContext';
import { PhraseCommonness } from 'client/types';
import { getDefaultSkillName } from 'client/utils/settings/alice-skill';
import * as keyset from 'i18n/GeneralSettings';

const t = i18n(keyset);

interface TextInputWithCommonnessProps {
    value: string;
    disabled?: boolean;
    onUpdate: (val: string) => void;
    spinnerGapRight?: boolean;
    hideCommonness?: boolean;
    className: string;
    placeholder: string;
}

export const TextInputWithCommonness: React.FC<TextInputWithCommonnessProps> = ({
    value,
    spinnerGapRight,
    onUpdate,
    hideCommonness,
    className,
    placeholder,
    ...restProps
}) => {
    const [commonnessError, setCommonnessError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const inputRef: React.LegacyRef<HTMLInputElement> = useRef(null);
    const { setNonValidationError } = useContext(FormFieldContext);
    const isMounted = useMountedState();

    const getCommonness = async () => {
        if (!value || restProps.disabled || value === getDefaultSkillName() || hideCommonness) {
            setNonValidationError('');
            return;
        }

        setIsLoading(true);
        setCommonnessError('');

        try {
            const commonness = await getPhraseCommonness(value);
            if (isMounted() && commonness === PhraseCommonness.Red) {
                setNonValidationError(getCommonnessError('name'));
                setCommonnessError(commonness);
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setNonValidationError('');
        if (inputRef.current && !inputRef.current.focus) {
            void getCommonness();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, hideCommonness]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => void getCommonness(), []);

    return (
        <div>
            <TextInput
                className={className}
                size="xl"
                onBlur={getCommonness}
                value={value}
                onUpdate={(text: string) => {
                    setNonValidationError('');
                    onUpdate(text);
                }}
                ref={inputRef}
                autoComplete={false}
                error={!!commonnessError}
                errorMessage={commonnessError ? t('too-common-error') : undefined}
                placeholder={placeholder}
                {...restProps}
            />
            {isLoading ? <Loader size="s" /> : null}
        </div>
    );
};
